import * as React from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

import { useTransition, animated } from "react-spring";

const Background = styled(animated.div)`
  position: "fixed";
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
`;

type OverlayContextType = {
  showOverlay: (element: React.ReactElement<any>) => void;
  hideOverlay: () => void;
};

export const OverlayContext = React.createContext({} as OverlayContextType);

function OverlayManager({ children }: { children: any }) {
  const [currentOverlay, setCurrentOverlay] = React.useState<React.ReactElement<
    any
  > | null>(null);

  const [element, setElement] = React.useState<HTMLElement | null>(null);
  const [animationComplete, setAnimationComplete] = React.useState(false);

  React.useEffect(() => {
    const element = document.createElement("div");
    element.style.position = "fixed";
    element.style.top = "0px";
    element.style.left = "0px";
    element.style.zIndex = "1000";
    document.body.appendChild(element);
    setElement(element);

    return () => {
      document.body.removeChild(element);
    };
  }, []);

  function hideOverlay() {
    setCurrentOverlay(null);
    setAnimationComplete(false);
    document.body.style.overflow = null;
  }

  React.useEffect(() => {
    function onKeyDown(evt: KeyboardEvent) {
      if (currentOverlay && evt.key === "Escape") {
        hideOverlay();
      }
    }

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [currentOverlay]);

  const actions = React.useMemo(() => {
    function showOverlay(element: React.ReactElement<any>) {
      setCurrentOverlay(element);
      document.body.style.overflow = "hidden";
    }

    return {
      showOverlay,
      hideOverlay
    };
  }, []);

  const background = useTransition(Boolean(currentOverlay), null, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 }
  });

  const animatedRef = React.useRef<any | null>(null);

  const overlay = useTransition(currentOverlay, null, {
    from: { opacity: 0, transform: "translateX(100%)" },
    enter: { opacity: 1, transform: "translateX(0%)" },
    leave: { opacity: 0, transform: "translateX(100%)" },
    onRest: () => {
      if (animatedRef.current) {
        setTimeout(() => {
          setAnimationComplete(true);
        }, 10);
      }
    },

    config: {
      tension: 300,
      clamp: true
    }
  } as any);

  return (
    <>
      <OverlayContext.Provider value={actions}>
        {children}
        {element &&
          createPortal(
            <>
              {background.map(
                ({ item, key, props }) =>
                  item && (
                    <Background onClick={hideOverlay} key={key} style={props} />
                  )
              )}
              {overlay.map(({ item, key, props }) => {
                return (
                  item &&
                  React.cloneElement(item, {
                    key,
                    ref: animatedRef,
                    style: {
                      position: "fixed",
                      right: 0,
                      top: 0,
                      bottom: 0,
                      boxShadow: "-5px 0px 30px 0px rgba(0, 0, 0, 0.07)",
                      ...props,
                      transform: animationComplete
                        ? "none"
                        : (props as any).transform
                    }
                  })
                );
              })}
            </>,
            element
          )}
      </OverlayContext.Provider>
    </>
  );
}

export default OverlayManager;
